import { FC, useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import axiosService from '../../utils/axios';
import { useDebounce } from '../../hooks/usePerformanceOptimizations';
import ArtistPicker, { Artist, ArtistGenreLink } from './ArtistPicker';
import { Chip, INPUT_CLASS, SectionLabel } from './OnboardingUI';
import { formatGenreLabel } from '../../utils/genreLabel';
import { artistsInGenre, filterGenres, genreChipList, genrePickHint, MAX_TASTE_GENRES, removeGenreArtistsPrompt } from './onboardingLogic';

interface TastePickerProps {
    genres: string[];
    artists: Artist[];
    onGenresChange: (genres: string[]) => void;
    onArtistsChange: (artists: Artist[]) => void;
    dimArtistsUntilReady?: boolean;
}

interface PendingArtistRemoval {
    genre: string;
    keys: string[];
}

const artistIdentity = (artist: Artist) => (
    artist.id ? `id:${artist.id}` : `name:${(artist.displayText || artist.name || '').trim().toLowerCase()}`
);

const TastePicker: FC<TastePickerProps> = ({ genres, artists, onGenresChange, onArtistsChange, dimArtistsUntilReady = false }) => {
    const [genreQuery, setGenreQuery] = useState('');
    const [genreExtras, setGenreExtras] = useState<string[]>([]);
    const [pendingRemoval, setPendingRemoval] = useState<PendingArtistRemoval | null>(null);
    const debouncedGenreQuery = useDebounce(genreQuery, 300);
    const hadGenres = useRef(genres.length > 0);
    const artistsRef = useRef(artists);
    artistsRef.current = artists;

    useEffect(() => {
        const query = debouncedGenreQuery.trim();
        if (!query) return undefined;
        let cancelled = false;
        axiosService.get(`/spotify/genres?query=${encodeURIComponent(query)}&limit=12`)
            .then((response) => {
                if (cancelled) return;
                const remote = (response.data?.genres || [])
                    .map((genre: { displayText?: string }) => formatGenreLabel(genre.displayText || ''))
                    .filter(Boolean);
                setGenreExtras((current) => Array.from(new Set([...current, ...remote])));
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [debouncedGenreQuery]);

    useEffect(() => {
        const hasGenres = genres.length > 0;
        if (hasGenres && !hadGenres.current) {
            document.getElementById('onboarding-artists')?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        }
        hadGenres.current = hasGenres;
    }, [genres.length]);

    const sameGenre = (left: string, right: string) => left.toLowerCase() === right.toLowerCase();

    const pendingArtists = pendingRemoval
        ? artists.filter((artist) => pendingRemoval.keys.includes(artistIdentity(artist)))
        : [];

    useEffect(() => {
        if (pendingRemoval && pendingArtists.length === 0) setPendingRemoval(null);
    }, [pendingRemoval, pendingArtists.length]);

    const rememberGenreLinks = (links: ArtistGenreLink[]) => {
        const byId = new Map(links.map((link) => [link.id, link.genres]));
        const current = artistsRef.current;
        let changed = false;
        const next = current.map((artist) => {
            const incoming = artist.id ? byId.get(artist.id) : undefined;
            if (!incoming?.length) return artist;
            const merged = Array.from(new Set([...(artist.matchedGenres || []), ...incoming]));
            if (merged.length === (artist.matchedGenres || []).length) return artist;
            changed = true;
            return { ...artist, matchedGenres: merged };
        });
        if (changed) onArtistsChange(next);
    };

    const toggleGenre = (genre: string) => {
        if (genres.some((item) => sameGenre(item, genre))) {
            onGenresChange(genres.filter((item) => !sameGenre(item, genre)));
            const inside = artistsInGenre(artists, genre);
            if (inside.length) {
                setPendingRemoval({ genre, keys: inside.map(artistIdentity) });
            } else if (pendingRemoval && sameGenre(pendingRemoval.genre, genre)) {
                setPendingRemoval(null);
            }
            return;
        }
        if (genres.length >= MAX_TASTE_GENRES) return;
        if (pendingRemoval && sameGenre(pendingRemoval.genre, genre)) setPendingRemoval(null);
        onGenresChange([...genres, formatGenreLabel(genre)]);
    };

    const removePendingArtists = () => {
        if (!pendingRemoval) return;
        const drop = new Set(pendingRemoval.keys);
        onArtistsChange(artists.filter((artist) => !drop.has(artistIdentity(artist))));
        setPendingRemoval(null);
    };

    const genreChips = genreChipList(filterGenres(genreQuery, genreExtras), genres);
    const atCap = genres.length >= MAX_TASTE_GENRES;

    return (
        <div>
            <SectionLabel hint={genrePickHint(genres.length)}>Genres</SectionLabel>
            <div className="relative mb-4">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
                <input
                    aria-label="Search genres"
                    value={genreQuery}
                    onChange={(event) => setGenreQuery(event.target.value)}
                    placeholder="Search any genre, like shoegaze or afrobeats"
                    className={INPUT_CLASS}
                />
            </div>
            <div className="chip-pool chip-remove-row flex flex-wrap gap-2">
                {genreChips.map((genre) => {
                    const selected = genres.some((item) => sameGenre(item, genre));
                    return (
                        <Chip
                            key={genre}
                            label={formatGenreLabel(genre)}
                            selected={selected}
                            removable
                            disabled={!selected && atCap}
                            onToggle={() => toggleGenre(genre)}
                        />
                    );
                })}
                {genreChips.length === 0 && <p className="text-sm text-gray-400">No genres match that search.</p>}
            </div>
            {pendingRemoval && pendingArtists.length > 0 && (
                <div
                    className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-purple-light/20 bg-purple-light/5 px-3 py-2.5"
                    role="status"
                >
                    <p className="text-sm font-semibold text-gray-800">
                        {removeGenreArtistsPrompt(pendingArtists.length, pendingRemoval.genre)}
                    </p>
                    <span className="flex items-center gap-2">
                        <button type="button" className="taste-prompt-no" onClick={() => setPendingRemoval(null)}>
                            No
                        </button>
                        <button type="button" className="taste-prompt-yes" onClick={removePendingArtists}>
                            Remove
                        </button>
                    </span>
                </div>
            )}
            {(genres.length > 0 || artists.length > 0) && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                    <ArtistPicker genres={genres} selected={artists} onChange={onArtistsChange} onGenreLinks={rememberGenreLinks} dimWhileLoading={dimArtistsUntilReady} />
                </div>
            )}
        </div>
    );
};

export default TastePicker;
